import { useState, useRef } from "react";
import { Upload, X, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { TID } from "@/constants/testIds";

const SERVICE_OPTIONS = [
  { value: "commercial", label: "Commercial Cleaning" },
  { value: "move_in_out", label: "Apartment Move-In / Move-Out" },
  { value: "post_construction", label: "Post-Construction Cleanup" },
];

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service_type: "commercial", property_type: "",
    square_footage: "", address: "", preferred_date: "",
    message: "",
  });
  const [files, setFiles] = useState([]); // [{path, filename, preview, uploading}]
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (selected) => {
    const arr = Array.from(selected);
    for (const file of arr) {
      const localId = Math.random().toString(36).slice(2);
      const preview = URL.createObjectURL(file);
      setFiles((prev) => [...prev, { id: localId, filename: file.name, preview, uploading: true, path: null }]);
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => prev.map((f) => f.id === localId ? { ...f, path: data.path, uploading: false } : f));
      } catch (err) {
        setFiles((prev) => prev.filter((f) => f.id !== localId));
        toast.error(`Upload failed: ${file.name}`);
      }
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please fill in name, email, phone, and service type.");
      return;
    }
    if (files.some((f) => f.uploading)) {
      toast.error("Wait for images to finish uploading.");
      return;
    }
    setSubmitting(true);
    try {
      const cleanForm = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );
      const payload = { ...cleanForm, image_paths: files.map((f) => f.path).filter(Boolean) };
      await api.post("/quote-requests", payload);
      setSuccess(true);
      toast.success("Quote request received! We'll respond within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="quote" className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center fade-up" data-testid={TID.qfSuccess}>
          <div className="h-20 w-20 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 mx-auto flex items-center justify-center">
            <CheckCircle2 size={36} className="text-[#D4AF37]" strokeWidth={1.2} />
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-white mt-8">Request received.</h2>
          <p className="mt-5 text-white/65 text-lg">
            Thanks, <span className="text-white">{form.name}</span>. We're reviewing your details and you'll receive a custom quote by email within 24 hours.
          </p>
          <button
            onClick={() => { setSuccess(false); setForm({ name: "", email: "", phone: "", service_type: "commercial", property_type: "", square_footage: "", address: "", preferred_date: "", message: "" }); setFiles([]); }}
            className="btn-ghost mt-10"
          >
            Submit another request
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="py-24 lg:py-32 bg-[#050505] relative">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">
        <Reveal className="mb-14 max-w-2xl">
          <div className="overline text-[#D4AF37] mb-4">Request a Quote</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            Tell us about <span className="gold-grad-text italic">the space.</span>
          </h2>
          <p className="mt-5 text-white/65 text-lg">
            Free, no-obligation custom quote — emailed back within 24 hours. Photos help, but aren't required.
          </p>
        </Reveal>

        <Reveal delay={150}>
        <form onSubmit={submit} className="card-surface rounded-3xl p-7 lg:p-12 space-y-10">
          {/* Contact */}
          <div>
            <div className="overline text-white/50 mb-6 flex items-center gap-3">
              <span className="font-serif-display text-[#D4AF37] text-lg">01</span> Your Contact
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="overline text-white/50 block mb-2">Full Name *</label>
                <input className="input-line" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={TID.qfName} required />
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Phone *</label>
                <input className="input-line" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid={TID.qfPhone} required />
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-white/50 block mb-2">Email *</label>
                <input className="input-line" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid={TID.qfEmail} required />
              </div>
            </div>
          </div>

          {/* Service */}
          <div>
            <div className="overline text-white/50 mb-6 flex items-center gap-3">
              <span className="font-serif-display text-[#D4AF37] text-lg">02</span> Service Details
            </div>
            <label className="overline text-white/50 block mb-3">Service Type *</label>
            <div className="grid sm:grid-cols-3 gap-3" data-testid={TID.qfServiceType}>
              {SERVICE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => set("service_type", o.value)}
                  data-testid={`qf-service-${o.value}`}
                  className={`px-4 py-3 rounded-lg border text-sm text-left transition-all ${
                    form.service_type === o.value
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                      : "border-white/10 text-white/70 hover:border-white/30"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mt-8">
              <div>
                <label className="overline text-white/50 block mb-2">Property Type</label>
                <input className="input-line" value={form.property_type} onChange={(e) => set("property_type", e.target.value)} placeholder="Office, Apartment, Site..." data-testid={TID.qfPropertyType}/>
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Square Footage</label>
                <input className="input-line" value={form.square_footage} onChange={(e) => set("square_footage", e.target.value)} placeholder="e.g. 2,400" data-testid={TID.qfSqft}/>
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-white/50 block mb-2">Property Address</label>
                <input className="input-line" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid={TID.qfAddress}/>
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Preferred Date</label>
                <input type="date" className="input-line" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid={TID.qfDate}/>
              </div>
            </div>

            <div className="mt-8">
              <label className="overline text-white/50 block mb-2">Notes / Scope</label>
              <textarea className="input-line" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything specific we should know — access, finishes, problem areas..." rows={4} data-testid={TID.qfMessage}/>
            </div>
          </div>

          {/* Uploads */}
          <div>
            <div className="overline text-white/50 mb-6 flex items-center gap-3">
              <span className="font-serif-display text-[#D4AF37] text-lg">03</span> Photos (Optional)
            </div>
            <input
              ref={fileInputRef}
              data-testid={TID.qfUploadInput}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid={TID.qfUpload}
              className="w-full border border-dashed border-white/15 hover:border-[#D4AF37]/60 rounded-2xl py-10 px-6 text-center transition-colors"
            >
              <Upload size={28} className="mx-auto text-[#D4AF37]" strokeWidth={1.2} />
              <div className="mt-4 text-white">Tap to upload photos</div>
              <div className="text-xs text-white/40 mt-1">JPG, PNG, WEBP · up to 10MB each</div>
            </button>

            {files.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
                {files.map((f) => (
                  <div key={f.id} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 group">
                    <img src={f.preview} alt={f.filename} className="w-full h-full object-cover" />
                    {f.uploading && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <Loader2 className="animate-spin text-[#D4AF37]" size={22} />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove"
                    >
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4 border-t border-white/5">
            <p className="text-sm text-white/50">By submitting, you agree to be contacted about your request.</p>
            <button type="submit" disabled={submitting} className="btn-gold" data-testid={TID.qfSubmit}>
              {submitting ? <><Loader2 className="animate-spin" size={16}/> Sending...</> : <>Send Request <ArrowRight size={16}/></>}
            </button>
          </div>
        </form>
        </Reveal>
      </div>
    </section>
  );
};
